/**
 * Month-on-month projection (the runway view).
 *
 * Pure and deterministic, like the rest of the engine. It runs `computePayday`
 * for a run of consecutive months, carrying the value-holding balances
 * (accounts, cards and pots) forward from each month into the next. So leftover
 * cash accumulates, loans and payment plans draw down, and pots build up,
 * giving an honest picture of how the cash position moves over time.
 *
 * One-offs are tied to the single month they fall due (the engine already gates
 * them by date), so a one-off shows up as a one-time dip or lift in exactly its
 * month and never recurs across the projection.
 */
import { computePayday } from "./engine.js";
import { addMonths } from "./months.js";
import { convertMinor, hasRate, sumPence, ZERO } from "./money.js";
/** Account roles whose balance is spendable cash (mirrors the engine). */
const SPENDABLE_ROLES = new Set(["income_hub", "spending"]);
/** Sum the spendable balances, converted into the primary currency. */
function spendableLiquid(nodes, primaryCurrency, rates) {
    return sumPence(nodes
        .filter((node) => node.type === "account" &&
        SPENDABLE_ROLES.has(node.meta.role) &&
        hasRate(node.currency, primaryCurrency, rates))
        .map((node) => node.type === "account"
        ? convertMinor(node.meta.balance, node.currency, primaryCurrency, rates)
        : ZERO));
}
/** Build next month's nodes by writing this month's ending balances back. */
function carryForward(nodes, balances) {
    const balanceById = new Map(balances.map((entry) => [entry.nodeId, entry.balance]));
    return nodes.map((node) => {
        const next = balanceById.get(node.id);
        if (next === undefined) {
            return node;
        }
        switch (node.type) {
            case "account":
                return { ...node, meta: { ...node.meta, balance: next } };
            case "pot":
                return { ...node, meta: { ...node.meta, balance: next } };
            case "card": {
                // A card's balance is debt: once cleared it cannot go below zero. Any
                // balance still carried after this month's payment grows by one month's
                // interest (a twelfth of the annual rate, held in basis points), so a
                // balance you do not clear builds up realistically. A 0% plan or a
                // cleared balance accrues nothing.
                const carried = Math.max(0, next);
                const interest = Math.round((carried * node.meta.aprBasisPoints) / (10000 * 12));
                return { ...node, meta: { ...node.meta, balance: (carried + interest) } };
            }
            default:
                return node;
        }
    });
}
export function projectRunway(input) {
    const horizon = Math.max(1, Math.floor(input.horizon));
    const primaryCurrency = input.primaryCurrency ?? "GBP";
    const rates = input.rates;
    const openingLiquid = spendableLiquid(input.nodes, primaryCurrency, rates);
    const snapshots = [];
    let nodes = input.nodes;
    let previousLiquid = openingLiquid;
    let shortfallMonth = null;
    let lowestLiquid = openingLiquid;
    for (let index = 0; index < horizon; index += 1) {
        const month = addMonths(input.startMonth, index);
        const result = computePayday({
            nodes,
            flows: input.flows,
            context: { month, primaryCurrency, rates },
        });
        // With balances carried forward, safe-to-spend is the running cash position.
        const liquid = result.safeToSpend;
        const netChange = (liquid - previousLiquid);
        snapshots.push({ month, liquid, netChange, warnings: result.warnings });
        if (shortfallMonth === null && liquid < 0) {
            shortfallMonth = month;
        }
        if (liquid < lowestLiquid) {
            lowestLiquid = liquid;
        }
        previousLiquid = liquid;
        nodes = carryForward(nodes, result.balances);
    }
    const runwayMonths = shortfallMonth === null ? null : snapshots.findIndex((s) => s.month === shortfallMonth);
    return { snapshots, openingLiquid, shortfallMonth, runwayMonths, lowestLiquid };
}
/**
 * Compute a single month as it actually stands, having carried balances forward
 * from the ledger's start month. Viewing a later month then shows balances that
 * reflect every month's payments and income in between. A month at or before the
 * start carries nothing and is computed from the nodes as given.
 */
export function computeForMonth(params) {
    const { nodes, flows, startMonth, month, primaryCurrency, rates } = params;
    let openingNodes = nodes;
    let cursor = startMonth;
    // Months compare correctly as YYYY-MM strings. The cap is a safety net against
    // a pathological range; real horizons are small.
    let steps = 0;
    while (cursor < month && steps < 600) {
        const stepResult = computePayday({
            nodes: openingNodes,
            flows,
            context: { month: cursor, primaryCurrency, rates },
        });
        openingNodes = carryForward(openingNodes, stepResult.balances);
        cursor = addMonths(cursor, 1);
        steps += 1;
    }
    const result = computePayday({
        nodes: openingNodes,
        flows,
        context: { month, primaryCurrency, rates },
    });
    return { openingNodes, result };
}
//# sourceMappingURL=projection.js.map